import axios from 'axios';
import { logoutUser } from '../Context/authLogout';
import config from '../config';

const _axiosInstance = axios.create({
    baseURL: config.api.baseURL,
    timeout: config.api.timeout,
    headers: {
        'Content-Type': 'application/json'
    }
});

const LOGOUT_FLAG = "LOGGED_OUT";
const isUserLoggedOut = () => localStorage.getItem(LOGOUT_FLAG) === "true";
const setLogoutFlag = () => localStorage.setItem(LOGOUT_FLAG, "true");
export const clearLogoutFlag = () => localStorage.removeItem("LOGGED_OUT");

const getMessage = (data) => {
    if (!data) return null;

    const entry = Object.entries(data)
        .find(([key]) => key.toLowerCase() === 'zmessage' || key.toLowerCase() === 'message');

    return entry?.[1] ?? null;
};

_axiosInstance.interceptors.response.use(
    async (response) => {
        if (!response.config?.skipSessionCheck) {
            checkSession(getMessage(response?.data));
        }

        return response;
    },
    async (error) => {
        const originalRequest = error.config || {};
        if (!originalRequest?.skipSessionCheck) {
            checkSession(getMessage(error?.response?.data));
        }

        if (!originalRequest._retried) {
            originalRequest._retried = true;

            if (!error.response || error.response.status >= 500) {
                await new Promise(resolve => setTimeout(resolve, retryDelay()));
                return _axiosInstance(originalRequest);
            }
        }

        return Promise.reject(error);
    }
);

_axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

function checkSession(zmessage) {
   
    if (!Array.isArray(zmessage) || zmessage.length === 0) return;

    const first = zmessage[0];

    const rawMessage =
        first?.RETURNMESSAGE ||
        first?.returnmessage ||
        "";

    const message = rawMessage.toLowerCase();

    const isAccountDeleted =
        rawMessage ===
        "Your Account no Longer Exists, Please contact Support for Assistance.";

    const isSessionIssue =
        message.includes("session") ||
        message.includes("already logged out") ||
        message.includes("invalid session") ||
        message.includes("session not found");

    if ((isAccountDeleted || isSessionIssue) && !isUserLoggedOut()) {
        setLogoutFlag();
        logoutUser("SESSION_EXPIRED");
    }
}

const retryDelay = () => {
    return 1000 + Math.random() * 1000;
};

let requestQueue = [];
let isProcessingQueue = false;
let pendingRequests = new Map();

function generateRequestKey(method, url, data) {
    let key = `${method}:${url}`;
    if (data && (method === 'post' || method === 'put' || method === 'patch')) {
        try {
            key += `:${JSON.stringify(data)}`;
        } catch {
            key += ':unstringifiable-data';
        }
    }
    return key;
}

async function processQueue() {
    if (isProcessingQueue || requestQueue.length === 0) return;

    isProcessingQueue = true;
    const { method, url, data, config, resolve, reject, requestKey } = requestQueue.shift();

    try {
        let res;
        switch (method) {
            case 'get': res = await _axiosInstance.get(url, config); break;
            case 'post': res = await _axiosInstance.post(url, data, config); break;
            case 'put': res = await _axiosInstance.put(url, data, config); break;
            case 'delete': res = await _axiosInstance.delete(url, config); break;
            case 'patch': res = await _axiosInstance.patch(url, data, config); break;
            default: throw new Error(`Unsupported method: ${method}`);
        }
        resolve(res);
    } catch (err) {
        reject(err);
    } finally {
        isProcessingQueue = false;
        pendingRequests.delete(requestKey);
        processQueue();
    }
}

function enqueueRequest(method, url, data, config) {
    const requestKey = generateRequestKey(method, url, data);
    if (pendingRequests.has(requestKey)) {
        return pendingRequests.get(requestKey);
    }

    const promise = new Promise((resolve, reject) => {
        requestQueue.push({ method, url, data, config, resolve, reject, requestKey });
        processQueue();
    });

    pendingRequests.set(requestKey, promise);
    return promise;
}

const httpClient = {
    get: (url, config) => enqueueRequest('get', url, undefined, config),
    post: (url, data, config) => enqueueRequest('post', url, data, config),
    put: (url, data, config) => enqueueRequest('put', url, data, config),
    delete: (url, config) => enqueueRequest('delete', url, undefined, config),
    patch: (url, data, config) => enqueueRequest('patch', url, data, config),
    interceptors: _axiosInstance.interceptors
};

export default httpClient;
